/** KPI tiles of the preview, mirroring the Dashboard's own three headline stats. */
const PREVIEW_KPIS = [
  { label: 'Total documents', value: '41,286', delta: '▲ 8.2%' },
  { label: 'Auto-classified', value: '87.4%', delta: '▲ 2.1pt' },
  { label: 'Needs review', value: '6.2%', delta: '▼ 0.8pt' },
] as const;

/** The type-mix bars: label, share of the widest bar, count. */
const PREVIEW_MIX = [
  { label: 'Invoice', width: '100%', count: '14.1k', colour: 'var(--c1)' },
  { label: 'Purch. Order', width: '59%', count: '8.3k', colour: 'var(--c2)' },
  { label: 'Delivery', width: '49%', count: '6.9k', colour: 'var(--c3)' },
] as const;

/** The volume sparkline of the preview, verbatim from the landing mock. */
const SPARK_LINE =
  'M0,64 L33,58 L66,66 L99,52 L132,57 L165,45 L198,53 L231,40 L264,47 L297,36 L330,44 L363,30 L396,38 L429,24 L460,29';

export interface LandingPreviewProps {
  /** Initial of the resolved brand, shown on the preview's window mark. */
  brandInitial: string;
}

/**
 * The hero's dashboard screenshot, drawn rather than photographed so it themes
 * and scales with the tokens. Purely decorative: it is `aria-hidden`, and every
 * number in it is illustrative sample data, never a live figure.
 */
export function LandingPreview({ brandInitial }: LandingPreviewProps): React.JSX.Element {
  return (
    <div className="landing-preview" aria-hidden="true">
      <span className="landing-preview-glow" />
      <div className="landing-preview-window">
        <div className="landing-preview-bar">
          <span className="landing-mark landing-mark--xs">{brandInitial}</span>
          <span className="landing-preview-title">Dashboard</span>
          <span className="landing-preview-chip">Acme Ltd · last 30 days</span>
        </div>
        <div className="landing-preview-body">
          <div className="landing-preview-kpis">
            {PREVIEW_KPIS.map((kpi) => (
              <div className="landing-preview-kpi" key={kpi.label}>
                <div className="landing-preview-kpi-label">{kpi.label}</div>
                <div className="landing-preview-kpi-value mono">{kpi.value}</div>
                <div className="landing-preview-kpi-delta">{kpi.delta}</div>
              </div>
            ))}
          </div>

          <div className="landing-preview-tile">
            <div className="landing-preview-tile-title">Volume over time</div>
            <svg width="100%" height="90" viewBox="0 0 460 90" preserveAspectRatio="none" fill="none">
              <line x1="0" y1="22" x2="460" y2="22" stroke="var(--grid)" />
              <line x1="0" y1="48" x2="460" y2="48" stroke="var(--grid)" />
              <line x1="0" y1="74" x2="460" y2="74" stroke="var(--grid)" />
              <path d={`${SPARK_LINE} L460,84 L0,84 Z`} fill="var(--a-soft)" />
              <path d={SPARK_LINE} stroke="var(--accent)" strokeWidth="2" />
            </svg>
          </div>

          <div className="landing-preview-split">
            <div className="landing-preview-tile">
              <div className="landing-preview-tile-title">Document type mix</div>
              <div className="landing-preview-mix">
                {PREVIEW_MIX.map((row) => (
                  <div className="landing-preview-mix-row" key={row.label}>
                    <span className="landing-preview-mix-label">{row.label}</span>
                    <span className="landing-preview-mix-track">
                      <span
                        className="landing-preview-mix-fill"
                        style={{ width: row.width, background: row.colour }}
                      />
                    </span>
                    <span className="mono">{row.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="landing-preview-tile landing-preview-opportunity">
              <span className="landing-preview-score">92</span>
              <span>
                <span className="landing-preview-opportunity-title">Invoices · Midtown</span>
                <span className="landing-preview-opportunity-note">Automate this first</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
